"use client";

import React, { useState } from "react";
import Link from "next/link";
import { Lock, Unlock, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { CookieSettingsButton, useCookieConsent } from "@/components/CookieConsent";
import { roomHeaders } from "@/lib/room-client";
import type { RoomAccess } from "../use-room-access";

export function ShoppingListUnlock({ designId, isUnlocked, onUnlocked, setLinkStatus }: {
  designId: string;
  isUnlocked: boolean;
  onUnlocked: (access: RoomAccess) => void;
  setLinkStatus: (message: string) => void;
}) {
  const { choices: cookieChoices, rememberedEmail, setRememberedEmail } = useCookieConsent();
  const [rememberEmail, setRememberEmail] = useState(true);
  const [unlockEmail, setUnlockEmail] = useState<string | null>(null);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [isUnlockModalOpen, setIsUnlockModalOpen] = useState(false);
  const [unlockError, setUnlockError] = useState("");

  const handleUnlockSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = (unlockEmail ?? rememberedEmail).trim();
    if (!email || !email.includes("@")) {
      alert("Please enter a valid email address.");
      return;
    }

    setUnlockLoading(true);
    setUnlockError("");
    try {
      const res = await fetch("/api/unlock", {
        method: "POST",
        headers: roomHeaders(designId, { "Content-Type": "application/json" }),
        body: JSON.stringify({
          designId,
          email,
          marketingConsent,
          rememberEmail: cookieChoices?.preferences === true && rememberEmail,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || "Could not save your shopping list.");
      onUnlocked(data);
      if (res.headers.get("X-IVA-Room-Cookie") !== "1" && data.roomLink) window.history.replaceState(null, "", data.roomLink);
      if (res.headers.get("X-IVA-Email-Remembered") === "1") {
        setRememberedEmail(email);
        setUnlockEmail(null);
      } else if (!rememberEmail) setRememberedEmail("");
      setLinkStatus(res.headers.get("X-IVA-Email-Remembered") === "0" ? "Your shopping list is saved, but we couldn't remember your email. You can try again next time." : data.message || "Your shopping list is saved.");
      setIsUnlockModalOpen(false);
    } catch (err) {
      setUnlockError(err instanceof Error ? err.message : "Could not save your shopping list. Please try again.");
    } finally {
      setUnlockLoading(false);
    }
  };

  return (
    <>
      {!isUnlocked && (
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 p-5 bg-clay/5 border-2 border-clay/10 rounded-2xl mb-6">
          <div>
            <h3 className="text-lg font-bold text-ink">Unlock your full shopping list & buy links</h3>
            <p className="text-xs text-ink-soft mt-1">Hero furniture pieces are currently locked.</p>
          </div>
          <Button onClick={() => setIsUnlockModalOpen(true)} size="md" className="w-full md:w-auto gap-2">
            <Lock className="w-4 h-4" />
            <span>Unlock for Free</span>
          </Button>
        </div>
      )}

      {isUnlockModalOpen && !isUnlocked && (
        <div role="dialog" aria-modal="true" aria-labelledby="unlock-title" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/60 backdrop-blur-sm animate-in fade-in zoom-in-95">
          <div className="bg-paper w-full max-w-md max-h-[90dvh] rounded-2xl shadow-xl overflow-y-auto relative">
            <button
              aria-label="Close email form"
              onClick={() => setIsUnlockModalOpen(false)}
              className="absolute top-4 right-4 text-ink-soft hover:text-ink bg-paper-deep rounded-full p-1 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="p-6 sm:p-8">
              <div className="w-12 h-12 bg-clay/10 rounded-full flex items-center justify-center mb-4 mx-auto">
                <Lock className="w-6 h-6 text-clay" />
              </div>
              <h3 id="unlock-title" className="text-xl font-bold text-ink text-center mb-2">
                Get your complete shopping list
              </h3>
              <p className="text-sm text-ink-soft text-center mb-6">
                Enter your email to immediately reveal all SKU titles, dimensions, and direct purchase links for this design.
              </p>

              <form onSubmit={handleUnlockSubmit} className="space-y-4">
                {unlockError && <p role="alert" className="text-sm text-error">{unlockError}</p>}
                <div className="space-y-2">
                  <input
                    type="email"
                    aria-label="Email address"
                    autoComplete="email"
                    required
                    placeholder="Email address"
                    value={unlockEmail ?? rememberedEmail}
                    onChange={(e) => setUnlockEmail(e.target.value)}
                    className="w-full px-4 py-3 bg-paper-deep rounded-xl border border-line text-sm focus:outline-none focus:ring-2 focus:ring-clay/40 transition-shadow"
                  />
                </div>
                {cookieChoices?.preferences ? <label className="flex items-start gap-2 text-xs text-ink-soft">
                  <input type="checkbox" checked={rememberEmail} onChange={event => setRememberEmail(event.target.checked)} className="mt-0.5 accent-clay" />
                  <span>Remember my email on this device for 90 days. Avoid this on a shared device.</span>
                </label> : <p className="text-xs text-ink-soft">To remember your email between visits, enable “Remember my details” in <CookieSettingsButton />.</p>}
                <p className="text-xs text-ink-soft">We use your email to save your shopping list. <Link href="/privacy" className="underline">How we use your information</Link>.</p>
                <label className="flex items-start gap-2 text-[11px] text-ink-soft cursor-pointer">
                  <input
                    type="checkbox"
                    checked={marketingConsent}
                    onChange={(e) => setMarketingConsent(e.target.checked)}
                    className="mt-0.5 rounded border-line text-clay focus:ring-clay/40"
                  />
                  <span>Send me occasional design updates and trade decor guides (optional)</span>
                </label>
                <Button type="submit" size="lg" disabled={unlockLoading} className="w-full gap-2 mt-2">
                  <Unlock className="w-4 h-4" />
                  <span>{unlockLoading ? "Unlocking..." : "Unlock Now (Free)"}</span>
                </Button>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
